import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCursor } from '../contexts/CursorContext';
import './ErosPoetry.css';

const ErosPoetry = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { setPageOverride } = useCursor();
  const [poems, setPoems] = useState([]);
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [isWriting, setIsWriting] = useState(false);
  const [newPoem, setNewPoem] = useState({ title: '', content: '' });
  const [isReading, setIsReading] = useState(false);
  const [wordsLoaded, setWordsLoaded] = useState(false);
  const textareaRef = useRef(null);

  // Override global custom cursor on this page - use default
  useEffect(() => {
    setPageOverride(true);
    return () => setPageOverride(false);
  }, [setPageOverride]);

  // Load poems from Firebase
  useEffect(() => {
    const poemsRef = ref(database, 'intimacy/eros/poems');
    const unsubscribe = onValue(poemsRef, (snapshot) => {
      if (snapshot.exists()) {
        const poemsData = snapshot.val();
        const poemsArray = Object.entries(poemsData).map(([id, poem]) => ({
          id,
          ...poem
        }));
        poemsArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPoems(poemsArray);
      } else {
        setPoems([]);
      }
    });

    return () => unsubscribe();
  }, []);

  // Trigger word animations when reading
  useEffect(() => {
    if (isReading && selectedPoem) {
      setWordsLoaded(false);
      const timer = setTimeout(() => {
        setWordsLoaded(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isReading, selectedPoem]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current && isWriting) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [newPoem.content, isWriting]);

  const handleSavePoem = async () => {
    if (!newPoem.title.trim() || !newPoem.content.trim()) return;

    const poemId = Date.now().toString();
    const poemRef = ref(database, `intimacy/eros/poems/${poemId}`);
    await set(poemRef, {
      title: newPoem.title.trim(),
      content: newPoem.content.trim(),
      author: userProfile?.name || 'Anonymous',
      createdAt: new Date().toISOString()
    });

    setNewPoem({ title: '', content: '' });
    setIsWriting(false);
  };

  const handlePoemClick = (poem) => {
    setSelectedPoem(poem);
    setIsReading(true);
  };

  const handleBack = () => {
    if (isReading) {
      setIsReading(false);
      setSelectedPoem(null);
      setWordsLoaded(false);
    } else if (isWriting) {
      setIsWriting(false);
      setNewPoem({ title: '', content: '' });
    } else {
      navigate('/intimacy', { state: { returnToEros: true } });
    }
  };

  // Split poem content into lines for animation
  const poemLines = selectedPoem?.content?.split('\n') || [];

  return (
    <div className="eros-poetry-page">
      {/* Animated background with ember particles */}
      <div className="eros-poetry-background">
        <div className="ember-glow" />
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="ember-particle"
            initial={{
              x: Math.random() * window.innerWidth,
              y: window.innerHeight + 50,
              opacity: 0,
              scale: Math.random() * 0.5 + 0.3
            }}
            animate={{
              y: -100,
              opacity: [0, 0.8, 0.6, 0],
              x: `+=${Math.random() * 100 - 50}`
            }}
            transition={{
              duration: Math.random() * 8 + 8,
              repeat: Infinity,
              delay: Math.random() * 10,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* Back button */}
      <motion.button
        className="eros-poetry-back"
        onClick={handleBack}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isReading || isWriting ? '< Back' : '< Return to Intimacy'}
      </motion.button>

      <AnimatePresence mode="wait">
        {/* POEM LIST VIEW */}
        {!isReading && !isWriting && (
          <motion.div
            key="list"
            className="eros-poetry-list-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.h1
              className="eros-poetry-title"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Eros Poetry
            </motion.h1>

            <motion.p
              className="eros-poetry-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Words whispered in the dark
            </motion.p>

            <motion.button
              className="write-poem-btn"
              onClick={() => setIsWriting(true)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Write New Poem
            </motion.button>

            <div className="poems-grid">
              {poems.length === 0 ? (
                <motion.p
                  className="no-poems-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  No poems yet... Let your heart speak first.
                </motion.p>
              ) : (
                poems.map((poem, index) => (
                  <motion.div
                    key={poem.id}
                    className="poem-card-eros"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    onClick={() => handlePoemClick(poem)}
                  >
                    <h3 className="poem-card-title">{poem.title}</h3>
                    <p className="poem-card-preview">
                      {poem.content.substring(0, 100)}
                      {poem.content.length > 100 ? '...' : ''}
                    </p>
                    <div className="poem-card-footer">
                      <span className="poem-card-author">{poem.author}</span>
                      <span className="poem-card-date">
                        {new Date(poem.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* READING VIEW */}
        {isReading && selectedPoem && (
          <motion.div
            key="reading"
            className="eros-poetry-reading-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="reading-content">
              <motion.h2
                className="reading-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 1 }}
              >
                {selectedPoem.title}
              </motion.h2>

              <div className="reading-poem-container">
                {poemLines.map((line, index) => (
                  <motion.p
                    key={index}
                    className="reading-line"
                    initial={{ opacity: 0, y: 20 }}
                    animate={wordsLoaded ? {
                      opacity: 1,
                      y: 0,
                    } : { opacity: 0, y: 20 }}
                    transition={{
                      delay: index * 0.15,
                      duration: 0.8,
                      ease: "easeOut"
                    }}
                  >
                    {line || '\u00A0'}
                  </motion.p>
                ))}
              </div>

              <motion.div
                className="reading-author"
                initial={{ opacity: 0 }}
                animate={{ opacity: wordsLoaded ? 1 : 0 }}
                transition={{ delay: poemLines.length * 0.15 + 0.5, duration: 1 }}
              >
                {selectedPoem.author}
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* WRITING VIEW */}
        {isWriting && (
          <motion.div
            key="writing"
            className="eros-poetry-writing-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="writing-container"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <motion.input
                type="text"
                className="writing-title-input"
                placeholder="Title..."
                value={newPoem.title}
                onChange={(e) => setNewPoem({ ...newPoem, title: e.target.value })}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              />

              <motion.textarea
                ref={textareaRef}
                className="writing-content-input"
                placeholder="Let your heart speak..."
                value={newPoem.content}
                onChange={(e) => setNewPoem({ ...newPoem, content: e.target.value })}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              />

              <motion.div
                className="writing-actions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <motion.button
                  className="save-poem-eros"
                  onClick={handleSavePoem}
                  disabled={!newPoem.title.trim() || !newPoem.content.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Save Poem
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ErosPoetry;
